import { activityTreeApiClient } from '../../../api/client';
import type {
  ActivityBreakdown,
  ActivityBreakdownNested,
  ActivitySummary,
  ActivityTokenizedDto,
  FullActivityTreeResponse,
  CensoActivityChatRequest,
  CensoActivityChatResponse,
  CensoActivityHistoryResponse,
  CensoActivityResetResponse,
  CensoChatRequest,
  CensoChatResponse,
  CensoConnectRequest,
  CensoConnectResponse,
  CensoHistoryResponse,
  ItemDto,
  ItemRateDto,
  ItemsFilter,
  LeafListResponse,
  LibraryHistoryRecord,
  LibraryInfo,
  MutationPreviewRequest,
  MutationPreviewResponse,
  ProjectDto,
  RegionalLibraryItem,
  RegionalLibraryItemRaw,
  ResourceGroup,
  SaveLibraryRequest,
  SaveLibraryResponse,
  SimulateRequest,
  SimulateResponse,
  TemplatesDict,
  ToggleFavouriteResponse,
} from '../types';

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const RATE_ENGINE_QUERY_KEYS = {
  all: ['rateEngine'] as const,

  projects: () => [...RATE_ENGINE_QUERY_KEYS.all, 'projects'] as const,

  items: (filter?: ItemsFilter) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'items', filter ?? {}] as const,

  projectItems: (projectId: number) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'projectItems', projectId] as const,

  itemCrossRegion: (itemCode: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'itemCrossRegion', itemCode] as const,

  libraryItems: (region: string, siteName?: string, oppNo?: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'libraryItems', region, siteName ?? '', oppNo ?? ''] as const,

  censoHistory: (sessionId: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'censoHistory', sessionId] as const,

  censoActivityHistory: (sessionId: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'censoActivityHistory', sessionId] as const,

  libraryHistory: (region: string, siteName?: string, batchId?: string, oppNo?: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'libraryHistory', region, siteName ?? '', batchId ?? '', oppNo ?? ''] as const,

  activityInfo: (region: string, siteName?: string, oppNo?: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'activityInfo', region, siteName ?? '', oppNo ?? ''] as const,

  activitySummary: (region: string, siteName?: string, oppNo?: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'activitySummary', region, siteName ?? '', oppNo ?? ''] as const,

  activityTemplates: (region: string, siteName?: string, oppNo?: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'activityTemplates', region, siteName ?? '', oppNo ?? ''] as const,

  activityLeaves: (region: string, parentId: number, siteName?: string, oppNo?: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'activityLeaves', region, parentId, siteName ?? '', oppNo ?? ''] as const,

  activityBreakdown: (region: string, code: string, siteName?: string, oppNo?: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'activityBreakdown', region, code, siteName ?? '', oppNo ?? ''] as const,

  activityBreakdownNested: (region: string, code: string, siteName?: string, oppNo?: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'activityBreakdownNested', region, code, siteName ?? '', oppNo ?? ''] as const,

  activityBreakdownSimulated: (region: string, code: string, batchId: string, siteName?: string, oppNo?: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'activityBreakdownSimulated', region, code, batchId, siteName ?? '', oppNo ?? ''] as const,

  activityFull: (region: string, siteName?: string, oppNo?: string) =>
    [...RATE_ENGINE_QUERY_KEYS.all, 'activityFull', region, siteName ?? '', oppNo ?? ''] as const,
};

/** Query params that scope a request to a site library. `oppNo` is optional and
 *  only meaningful alongside `siteName` (exact-match disambiguator when two
 *  sites share a name) — omitted entirely when unset so existing calls are
 *  byte-for-byte unchanged. */
const siteScopeParams = (siteName?: string, oppNo?: string): Record<string, string> => ({
  ...(siteName ? { siteName } : {}),
  ...(oppNo ? { oppNo } : {}),
});

// ─── Library history normalization ─────────────────────────────────────────────

const pickField = (obj: Record<string, unknown>, ...keys: string[]): unknown => {
  for (const k of keys) {
    if (obj[k] !== undefined) return obj[k];
  }
  return undefined;
};

/** `GET /rate-engine/library/{region}/history` field names, confirmed
 *  against a live response — `action`/`type`/`code`/`name`, not the
 *  `actionType`/`entityKind`/`entityCode`/`entityName` names an earlier set
 *  of example payloads used for this same endpoint (that mismatch, not a
 *  missing/wrapped response, was why every reader of this data rendered
 *  blank against real records). Every field is read under several spellings
 *  — the confirmed real name first, the original example name and a
 *  `snake_case` variant as fallbacks — in case another response variant (or
 *  a future backend change) differs again. Normalizing here, once, rather
 *  than per-consumer, is what keeps `ChangeHistoryModal` and
 *  `rate-manager.tsx`'s "last updated" banner agreeing on the same fields —
 *  both call `getLibraryHistory` (below) for the same data. Also guarantees
 *  a unique `id` (used as `ChangeHistoryModal`'s table `rowKey`) even on the
 *  rare response that omits one. */
const normalizeLibraryHistoryRecord = (raw: unknown, index: number): LibraryHistoryRecord => {
  const r = (raw ?? {}) as Record<string, unknown>;
  const asString = (v: unknown): string => (v == null ? '' : String(v));
  return {
    id: (pickField(r, 'id') as number) ?? index,
    projectId: (pickField(r, 'projectId', 'project_id') as number) ?? 0,
    entityKind: asString(pickField(r, 'type', 'entityKind', 'entity_kind')),
    entityCode: asString(pickField(r, 'code', 'entityCode', 'entity_code')),
    entityName: asString(pickField(r, 'name', 'entityName', 'entity_name')),
    actionType: asString(pickField(r, 'action', 'actionType', 'action_type')),
    fieldChanged: (pickField(r, 'fieldChanged', 'field_changed') as string | null) ?? null,
    before: (pickField(r, 'before') as Record<string, unknown> | null) ?? null,
    after: (pickField(r, 'after') as Record<string, unknown> | null) ?? null,
    changedBy: asString(pickField(r, 'changedBy', 'changed_by')),
    changedAt: asString(pickField(r, 'changedAt', 'changed_at')),
    agentPrompt: (pickField(r, 'agentPrompt', 'agent_prompt') as string | null) ?? null,
    batchId: asString(pickField(r, 'batchId', 'batch_id')),
  };
};

// ─── API Functions ────────────────────────────────────────────────────────────

export const rateEngineApi = {
  /** GET /rate-engine/projects — Returns all libraries (regional and site projects). */
  getProjects: async (): Promise<ProjectDto[]> => {
    try {
      const response = await activityTreeApiClient.get<ProjectDto[]>('/rate-engine/projects');
      return response.data;
    } catch (error) {
      console.error('Error fetching rate engine projects:', error);
      throw error;
    }
  },

  /** GET /rate-engine/items — Returns all global items. Supports search and resource group filtering. */
  getItems: async (filter?: ItemsFilter): Promise<ItemDto[]> => {
    try {
      const response = await activityTreeApiClient.get<ItemDto[]>('/rate-engine/items', {
        params: filter,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching rate engine items:', error);
      throw error;
    }
  },

  /** GET /rate-engine/projects/{projectId}/items — Returns all items with their rates for a specific library. */
  getProjectItems: async (projectId: number): Promise<ItemRateDto[]> => {
    try {
      const response = await activityTreeApiClient.get<ItemRateDto[]>(
        `/rate-engine/projects/${projectId}/items`,
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching items for project ${projectId}:`, error);
      throw error;
    }
  },

  /** GET /rate-engine/items/{itemCode} — Returns rates for a single item across all regions. */
  getItemCrossRegion: async (itemCode: string): Promise<ItemRateDto[]> => {
    try {
      const response = await activityTreeApiClient.get<ItemRateDto[]>(`/rate-engine/items/${itemCode}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching cross-region rates for item "${itemCode}":`, error);
      throw error;
    }
  },

  /**
   * GET /rate-engine/library/{region} — Returns all active items with nett rates for a regional library.
   * Accepts a region code (e.g. `NW`) or dashed region name (e.g. `north-west`).
   * Response uses tokenized keys (`c`, `n`, `rg`, `r`, `u`, `f`) which are hydrated to full field names.
   */
  getRegionalLibraryItems: async (region: string, siteName?: string, oppNo?: string): Promise<RegionalLibraryItem[]> => {
    try {
      const response = await activityTreeApiClient.get<RegionalLibraryItemRaw[]>(
        `/rate-engine/library/${region}`,
        siteName ? { params: siteScopeParams(siteName, oppNo) } : undefined,
      );
      return response.data.map((item) => ({
        code: item.c,
        name: item.n,
        resourceGroup: item.rg as ResourceGroup,
        rate: item.r,
        unit: item.u,
        isFavourite: item.f,
        costCode: item.cc ?? null,
      }));
    } catch (error) {
      console.error(`Error fetching regional library items for "${region}":`, error);
      throw error;
    }
  },

  /**
   * PUT /rate-engine/library/{region}/favourite/{code} — Mark or unmark a library item as favourite.
   * Without siteName: regional library. With siteName: site-specific library.
   */
  toggleFavourite: async (
    region: string,
    code: string,
    isFavourite: boolean,
    siteName?: string,
    oppNo?: string,
  ): Promise<ToggleFavouriteResponse> => {
    try {
      const response = await activityTreeApiClient.put<ToggleFavouriteResponse>(
        `/rate-engine/library/${region}/favourite/${code}`,
        { isFavourite },
        siteName ? { params: siteScopeParams(siteName, oppNo) } : undefined,
      );
      return response.data;
    } catch (error) {
      console.error(`Error toggling favourite for item "${code}" in region "${region}":`, error);
      throw error;
    }
  },

  /**
   * GET /rate-engine/library/{region}/history — Audit log of confirmed DB changes.
   * Optional filters: siteName (site-specific project), batchId (single save batch).
   * Every consumer (`ChangeHistoryModal`, `rate-manager.tsx`'s "last updated"
   * banner) reads the normalized field names below — normalizing once here,
   * not per-consumer, is what keeps them agreeing with each other.
   */
  getLibraryHistory: async (
    region: string,
    siteName?: string,
    batchId?: string,
    oppNo?: string,
  ): Promise<LibraryHistoryRecord[]> => {
    try {
      const params: Record<string, string> = siteScopeParams(siteName, oppNo);
      if (batchId) params.batchId = batchId;
      const response = await activityTreeApiClient.get<unknown>(
        `/rate-engine/library/${region}/history`,
        Object.keys(params).length > 0 ? { params } : undefined,
      );
      // Defensive unwrap: the refactored endpoint's exact envelope isn't
      // confirmed — handle a plain array (the documented shape) or a
      // `{ data | items | records: [...] }` wrapper, same convention
      // `PageGroupAccordion` (`activity-tree.tsx`) already uses for
      // `getActivityLeaves`. Without this, a wrapped response silently
      // reaches `ChangeHistoryModal` as a non-array and renders no rows.
      const raw = response.data;
      const arr: unknown[] = Array.isArray(raw)
        ? raw
        : (() => {
            const wrapped = raw as { data?: unknown; items?: unknown; records?: unknown } | null | undefined;
            const unwrapped = wrapped?.data ?? wrapped?.items ?? wrapped?.records;
            return Array.isArray(unwrapped) ? unwrapped : [];
          })();
      return arr.map(normalizeLibraryHistoryRecord);
    } catch (error) {
      console.error(`Error fetching library history for region "${region}":`, error);
      throw error;
    }
  },

  /** POST /rate-engine/censo/connect — Start a Censo session with the given library items. */
  censoConnect: async (request: CensoConnectRequest): Promise<CensoConnectResponse> => {
    try {
      const response = await activityTreeApiClient.post<CensoConnectResponse>('/rate-engine/censo/connect', request);
      return response.data;
    } catch (error) {
      console.error('Error connecting to Censo:', error);
      throw error;
    }
  },

  /** POST /rate-engine/censo/chat — Send a natural-language message; receive an AI reply with a rate delta. */
  censoChat: async ({ signal, ...request }: CensoChatRequest): Promise<CensoChatResponse> => {
    try {
      // AI responses can be slow — use a dedicated 5-minute timeout instead of the global 30s default.
      const response = await activityTreeApiClient.post<CensoChatResponse>('/rate-engine/censo/chat', request, {
        timeout: 300_000,
        signal,
      });
      return response.data;
    } catch (error) {
      console.error('Error sending Censo chat message:', error);
      throw error;
    }
  },

  /**
   * POST /rate-engine/censo/activity/chat — Activity Assistant: searches a live
   * project and can WRITE to it (after the user confirms a pending operation),
   * unlike the library assistant's `censoChat` above. Chosen from the Rate
   * Assistant's "Add New Activity" action. No separate connect handshake — every
   * turn (including the first) is sent here; `project_id` only needs to be
   * present on the first message of a conversation.
   */
  censoActivityChat: async ({ signal, ...request }: CensoActivityChatRequest): Promise<CensoActivityChatResponse> => {
    try {
      // A live search over an activity used in thousands of places, plus an
      // Azure OpenAI turn, can take considerably longer than a normal request —
      // the server allows up to 10 minutes, so match that instead of the global default.
      const response = await activityTreeApiClient.post<CensoActivityChatResponse>(
        '/rate-engine/censo/activity/chat',
        request,
        { timeout: 600_000, signal },
      );
      return response.data;
    } catch (error) {
      console.error('Error sending Activity Assistant message:', error);
      throw error;
    }
  },

  /**
   * POST /rate-engine/censo/activity/reset — Ends an Activity Assistant conversation,
   * discarding all server-side state (resolved matches, mid-clarification state,
   * any unconfirmed pending change). Hygiene, not correctness — call it when the
   * user finishes (closes the chat, starts a new conversation, switches project).
   */
  censoActivityReset: async (sessionId: string): Promise<CensoActivityResetResponse> => {
    try {
      const response = await activityTreeApiClient.post<CensoActivityResetResponse>(
        '/rate-engine/censo/activity/reset',
        { session_id: sessionId },
      );
      return response.data;
    } catch (error) {
      console.error('Error resetting Activity Assistant session:', error);
      throw error;
    }
  },

  /** GET /rate-engine/censo/activity/history/{sessionId} — Activity Assistant session history (separate from the library assistant's). */
  getCensoActivityHistory: async (sessionId: string): Promise<CensoActivityHistoryResponse> => {
    try {
      const response = await activityTreeApiClient.get<CensoActivityHistoryResponse>(
        `/rate-engine/censo/activity/history/${sessionId}`,
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching Activity Assistant history for session "${sessionId}":`, error);
      throw error;
    }
  },

  /**
   * POST /rate-engine/activities/mutate/preview — Dry-run an add/modify/delete/move
   * mutation against the live activity tree without writing anything. Same request
   * shape as the real mutate endpoint — a `CensoActivityPendingOperation` (minus
   * `summary`) can be sent here as-is to preview its cost impact before the user
   * confirms it. No `{region}` segment: the project is derived from the ids inside
   * `placement_key`/`target_node_handle`. Unrelated to `simulateActivities*` above,
   * which is the library assistant's own preview mechanism.
   */
  previewActivityMutation: async (
    request: MutationPreviewRequest,
    signal?: AbortSignal,
  ): Promise<MutationPreviewResponse> => {
    try {
      const response = await activityTreeApiClient.post<MutationPreviewResponse>(
        '/rate-engine/activities/mutate/preview',
        request,
        { signal },
      );
      return response.data;
    } catch (error) {
      console.error('Error previewing activity mutation:', error);
      throw error;
    }
  },

  /** GET /rate-engine/censo/history/{sessionId} — Retrieve all exchanges for a session. */
  getCensoHistory: async (sessionId: string): Promise<CensoHistoryResponse> => {
    try {
      const response = await activityTreeApiClient.get<CensoHistoryResponse>(`/rate-engine/censo/history/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching Censo history for session "${sessionId}":`, error);
      throw error;
    }
  },

  /**
   * POST /rate-engine/library/{region}/refresh — Reset a site library back to the current
   * regional defaults. Irreversible. Only valid for site libraries (siteName required).
   */
  refreshSiteLibrary: async (
    region: string,
    siteName: string,
    refreshedBy: string,
    oppNo?: string,
  ): Promise<void> => {
    await activityTreeApiClient.post(
      `/rate-engine/library/${region}/refresh`,
      { refreshedBy },
      { params: siteScopeParams(siteName, oppNo) },
    );
  },

  /**
   * POST /rate-engine/library/{region} — Confirm and persist Censo rate changes.
   * Without siteName: updates the regional library.
   * With siteName: updates the site-specific library (auto-created if it doesn't exist).
   */
  saveLibraryChanges: async (
    region: string,
    request: SaveLibraryRequest,
    siteName?: string,
    oppNo?: string,
  ): Promise<SaveLibraryResponse> => {
    try {
      const response = await activityTreeApiClient.post<SaveLibraryResponse>(
        `/rate-engine/library/${region}`,
        request,
        siteName ? { params: siteScopeParams(siteName, oppNo) } : undefined,
      );
      return response.data;
    } catch (error) {
      console.error(`Error saving library changes for region "${region}":`, error);
      throw error;
    }
  },

  // ─── Activity Tree ────────────────────────────────────────────────────────

  /** GET /rate-engine/library/{region}/info — Library metadata (last updated, etc.) */
  getActivityInfo: async (region: string, siteName?: string, oppNo?: string): Promise<LibraryInfo> => {
    const response = await activityTreeApiClient.get<LibraryInfo>(
      `/rate-engine/library/${region}/info`,
      siteName ? { params: siteScopeParams(siteName, oppNo) } : undefined,
    );
    return response.data;
  },

  /** GET /rate-engine/library/{region}/activities/summary — L1+L2 tree shell + RG dashboard */
  getActivitySummary: async (region: string, siteName?: string, oppNo?: string): Promise<ActivitySummary> => {
    const response = await activityTreeApiClient.get<ActivitySummary>(
      `/rate-engine/library/${region}/activities/summary`,
      siteName ? { params: siteScopeParams(siteName, oppNo) } : undefined,
    );
    return response.data;
  },

  /** GET /rate-engine/library/{region}/activities/templates — Full template dict (cached) */
  getActivityTemplates: async (region: string, siteName?: string, oppNo?: string): Promise<TemplatesDict> => {
    const response = await activityTreeApiClient.get<TemplatesDict>(
      `/rate-engine/library/${region}/activities/templates`,
      siteName ? { params: siteScopeParams(siteName, oppNo) } : undefined,
    );
    return response.data;
  },

  /** GET /rate-engine/library/{region}/activities/leaves?parentId={id} — L3 leaves for one page group */
  getActivityLeaves: async (region: string, parentId: number, siteName?: string, oppNo?: string): Promise<LeafListResponse> => {
    const response = await activityTreeApiClient.get<LeafListResponse>(
      `/rate-engine/library/${region}/activities/leaves`,
      { params: { parentId, ...siteScopeParams(siteName, oppNo) } },
    );
    return response.data;
  },

  /** GET /rate-engine/library/{region}/activities/{code}/breakdown — Per-leaf breakdown (flat, v1) */
  getActivityBreakdown: async (region: string, code: string, siteName?: string, oppNo?: string): Promise<ActivityBreakdown> => {
    const response = await activityTreeApiClient.get<ActivityBreakdown>(
      `/rate-engine/library/${region}/activities/${code}/breakdown`,
      siteName ? { params: siteScopeParams(siteName, oppNo) } : undefined,
    );
    return response.data;
  },

  /** GET /rate-engine/library/{region}/activities/{code}/breakdown?nested=true — Fully-nested breakdown (v2) */
  getActivityBreakdownNested: async (region: string, code: string, siteName?: string, oppNo?: string): Promise<ActivityBreakdownNested> => {
    const response = await activityTreeApiClient.get<ActivityBreakdownNested>(
      `/rate-engine/library/${region}/activities/${code}/breakdown`,
      { params: { nested: 'true', ...siteScopeParams(siteName, oppNo) } },
    );
    return response.data;
  },

  /** POST /rate-engine/library/{region}/activities/simulate — Dry-run Censo changes against the activity tree */
  simulateActivities: async (
    region: string,
    request: SimulateRequest,
    siteName?: string,
    oppNo?: string,
  ): Promise<SimulateResponse> => {
    const response = await activityTreeApiClient.post<SimulateResponse>(
      `/rate-engine/library/${region}/activities/simulate`,
      request,
      siteName ? { params: siteScopeParams(siteName, oppNo) } : undefined,
    );
    return response.data;
  },

  /**
   * POST /rate-engine/library/{region}/activities/{code}/breakdown?nested=true — Simulated per-leaf breakdown.
   * Same path as the GET steady-state breakdown; POST + body=`{changes}` applies the simulation overrides.
   * Returns the same `ActivityBreakdownNested` shape with `before`/`effectiveLineCostBefore` on changed nodes.
   */
  simulateActivityBreakdown: async (
    region: string,
    code: string,
    request: SimulateRequest,
    siteName?: string,
    oppNo?: string,
  ): Promise<ActivityBreakdownNested> => {
    const response = await activityTreeApiClient.post<ActivityBreakdownNested>(
      `/rate-engine/library/${region}/activities/${code}/breakdown`,
      request,
      { params: { nested: 'true', ...siteScopeParams(siteName, oppNo) } },
    );
    return response.data;
  },

  /** POST /rate-engine/library/{region}/activities/simulate?nested=true — Dry-run with nested breakdown trees (v2) */
  simulateActivitiesNested: async (
    region: string,
    request: SimulateRequest,
    siteName?: string,
    oppNo?: string,
  ): Promise<SimulateResponse> => {
    const response = await activityTreeApiClient.post<SimulateResponse>(
      `/rate-engine/library/${region}/activities/simulate`,
      request,
      { params: { nested: 'true', ...siteScopeParams(siteName, oppNo) } },
    );
    return response.data;
  },

  /**
   * GET /rate-engine/library/{region}/activities?full=true — Full tree in one shot.
   * Returns all sections → page groups → leaves (with bk breakdown) plus the templates dict.
   * Payload uses single-letter tokenized keys; sections carry rolled-up `r`, leaves carry `bk.components`.
   */
  getFullActivityTree: async (region: string, siteName?: string, oppNo?: string): Promise<FullActivityTreeResponse> => {
    const params: Record<string, string> = { full: 'true', ...siteScopeParams(siteName, oppNo) };
    const response = await activityTreeApiClient.get<FullActivityTreeResponse>(
      `/rate-engine/library/${region}/activities`,
      { params },
    );
    return response.data;
  },
};

// Re-export new DTO types so importers can use them without a separate types import
export type { ActivityTokenizedDto, FullActivityTreeResponse };
